import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import { fetchProducts } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { realGallery } from "@/lib/productImage";

/**
 * One tap reorder from a refill reminder: /refill?items=variantId:qty,...
 * Puts those jars in the basket and goes straight to checkout.
 */
export default function Refill() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const { items: cartItems, addItem, setDrawerOpen, setQty } = useCart();
  const [missing, setMissing] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const wanted = (search.get("items") || "")
      .split(",")
      .map((s) => s.split(":"))
      .filter(([id, q]) => id && Number(q) > 0)
      .map(([id, q]) => ({ id, qty: Math.min(20, Number(q)) }));
    fetchProducts({})
      .then((d) => {
        const lookup = {};
        (d.products || []).forEach((p) => (p.variants || []).forEach((v) => { lookup[v.id] = { p, v }; }));
        const found = wanted.filter((w) => lookup[w.id] && lookup[w.id].v.stock > 0);
        if (!found.length) { setMissing(true); return; }
        found.forEach(({ id, qty }) => {
          const { p, v } = lookup[id];
          const inCart = cartItems.find((i) => i.variant_id === id);
          if (inCart) { setQty(id, qty); return; }
          addItem({
            variant_id: v.id, product_id: p.id || p.slug, product_name: p.name, variant_size: v.size,
            sku: v.sku, image: realGallery(p)[0], unit_price: v.price, mrp: v.mrp, slug: p.slug, quantity: qty,
          });
        });
        setDrawerOpen(false);
        // Refill links apply REFILL5; pre-order payment links pass code=NONE.
        const code = (search.get("code") || "REFILL5").toUpperCase();
        nav("/checkout", { replace: true, state: { applyCode: code === "NONE" ? null : code } });
      })
      .catch(() => setMissing(true));
    // Runs once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo title="Reorder" description="Reorder Karijeeva cold pressed coconut oil." noindex />
      <section className="bg-brand-parchment min-h-[60vh] flex items-center justify-center px-6 text-center">
        {missing ? (
          <div data-testid="refill-missing">
            <h1 className="font-display text-[32px] tracking-heading text-brand-ink">That size is not available right now.</h1>
            <p className="mt-3 font-body text-ink-muted">It may have sold out or changed. Pick from what is in stock today.</p>
            <Link to="/products" className="mt-6 inline-flex rounded-control bg-brand-gold px-6 py-3 font-body text-white">See what is in stock</Link>
          </div>
        ) : (
          <p className="font-body text-ink-muted">Getting your basket ready</p>
        )}
      </section>
    </>
  );
}
